// components/NotesApp.js - Refactored for workspace architecture
import React, { useState, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';
import LeftPanel from './LeftPanel';
import MainPanel from './MainPanel';
//import RightPanel from './RightPanel';
import { getCurrentUser } from './utils/authService';
import { PanelProvider, usePanelContext } from './contexts/PanelContext';
import SettingsModal from './MainPanel/SettingsModal';
import { ThemeProvider } from './contexts/ThemeContext';
import { authFetch } from './utils/authFetch';
import { CategoriesProvider } from './contexts/CategoriesContext';
import { useWorkspaces } from './contexts/WorkspacesContext';
import { NotesProvider } from './contexts/NotesContext';
import { EditorProvider } from './contexts/EditorContext';
import styles from './NotesApp.module.css';
import { useNavigate, useParams } from 'react-router-dom';

const NotesAppContent = () => {
  const { 
    workspaces,
    documents,
    selectedDocumentPublicId,
    selectDocument,
    createWorkspace,
    updateDocument,
    selectWorkspace,
    deleteWorkspace
  } = useWorkspaces();

  const navigate = useNavigate();
  const { publicId } = useParams();  // Read URL parameter
  
  const { 
    isRightPanelVisible, 
    toggleRightPanel,
    isLeftPanelVisible, 
    toggleLeftPanel
  } = usePanelContext();
  
  // Other state
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [activeDock, setActiveDock] = useState('note');
  const [activeContent, setActiveContent] = useState('notes');
  const [isImageUploadExpanded, setImageUploadExpanded] = useState(false);
  const [galleryRefreshTrigger, setGalleryRefreshTrigger] = useState(0);

  // Test auth
  useEffect(() => {
    const testAuth = async () => {
      try {
        const API_PREFIX = '/api';
        const response = await fetch(`${API_PREFIX}/auth/test-cookie`, {
          credentials: 'include'
        });
        const data = await response.json();
      } catch (error) {
        console.error('Auth test failed:', error);
      }
    };
    
    testAuth();
  }, []);

// Read URL parameter and select that document
// Handle URL parameter changes (after initial load)
useEffect(() => {
  if (publicId && selectedDocumentPublicId && publicId !== selectedDocumentPublicId) {
    // Find which workspace this document belongs to
    const doc = documents.find(d => d.public_id === publicId);
    if (doc?.workspace_public_id) {
      selectWorkspace(doc.workspace_public_id);
    }
  }
}, [publicId]);

useEffect(() => {
  if (selectedDocumentPublicId && selectedDocumentPublicId !== publicId) {
    navigate(`/notes/${selectedDocumentPublicId}`, { replace: true });
  }
}, [selectedDocumentPublicId, navigate, publicId]);

  // Handle document selection (was handleNoteSelect)
const handleDocumentSelect = useCallback((documentPublicId) => {
  selectDocument(documentPublicId);
  navigate(`/notes/${documentPublicId}`);  // ✅ UPDATE URL
  setActiveDock('note');
}, [selectDocument, navigate]);

  const triggerGalleryRefresh = useCallback(() => {
    setGalleryRefreshTrigger(prev => prev + 1);
  }, []);

  const handleCreateCategory = useCallback(async (name) => {
    const user = getCurrentUser();
    try {
      const response = await fetch(`/api/users/${user.user_id}/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ name, is_special: name === 'Main' })
      });

      const newCategory = await response.json();
      
    } catch (error) {
      console.error('Error creating category:', error);
    }
  }, []);

  const handleDeleteTitle = useCallback(() => {
    console.log('Delete title clicked');
  }, []);

  const handleToggleImageUpload = useCallback(() => setImageUploadExpanded(prev => !prev), []);

  const handleSidebarContentChange = useCallback((content) => {
    console.log('Sidebar content change:', content);
    setActiveContent(content);
  }, []);

  const handleContainerSwap = useCallback(() => {
    console.log('Container swap clicked');
  }, []);

  return (
    <div className={styles.notesAppContainer}>
      <LeftPanel
        onDocumentSelect={handleDocumentSelect}             // ✅ CORRECT: Document selection handler
        selectedDocumentPublicId={selectedDocumentPublicId} // ✅ CORRECT: Selected document ID
        onCreateCategory={handleCreateCategory}
        currentUser={getCurrentUser()}
      />
      
      <div className={styles.mainContentWrapper}>
        <MainPanel
          isImageUploadExpanded={isImageUploadExpanded}
          onToggleImageUpload={handleToggleImageUpload}
          activeDock={activeDock}
          setActiveDock={setActiveDock}
          isRightPanelVisible={isRightPanelVisible}
          isLeftPanelVisible={isLeftPanelVisible}
          toggleRightPanel={toggleRightPanel}
          toggleLeftPanel={toggleLeftPanel}
          onDeleteTitle={handleDeleteTitle}
          onSidebarContentChange={handleSidebarContentChange}
          activeContent={activeContent}
          onContainerSwap={handleContainerSwap}
          onOpenSettings={() => setIsSettingsModalOpen(true)}
          onImageUpload={triggerGalleryRefresh}
        />
      </div>
      
      {/* <RightPanel
          isVisible={isRightPanelVisible}
          selectedDocumentPublicId={selectedDocumentPublicId}  // ✅ CORRECT: Document ID, not note ID
          galleryRefreshTrigger={galleryRefreshTrigger}
        /> */}
      
      <SettingsModal 
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        onSave={(settings) => {
          console.log('Saving settings:', settings);
          setIsSettingsModalOpen(false);
        }}
      />
    </div>
  );
};

const NotesApp = () => {
  // Wrapper to provide workspace context before notes context
const WorkspacesContextWrapper = () => {
  const { 
    selectedWorkspacePublicId
  } = useWorkspaces();
  
  return (
    <NotesProvider
      selectedWorkspacePublicId={selectedWorkspacePublicId}
    >
      <EditorProvider>
        <NotesAppContent />
      </EditorProvider>
    </NotesProvider>
  );
};

  return (
    <PanelProvider>
      <CategoriesProvider>
        <ThemeProvider>
          <WorkspacesContextWrapper />
        </ThemeProvider>
      </CategoriesProvider>
    </PanelProvider>
  );
};

export default NotesApp;